import { type CountryCode, getCountries, parsePhoneNumberFromString } from 'libphonenumber-js/max';

const SUPPORTED_COUNTRY_CODES = new Set<string>(getCountries());

function isSupportedCountryCode(countryCode: string): countryCode is CountryCode {
    return SUPPORTED_COUNTRY_CODES.has(countryCode);
}

export function resolvePhoneNumberCountryCode(
    countryCode: string | null | undefined,
    fallback: CountryCode = 'AU',
): CountryCode {
    const normalisedCountryCode = countryCode?.trim().toUpperCase();
    if (normalisedCountryCode && isSupportedCountryCode(normalisedCountryCode)) {
        return normalisedCountryCode;
    }

    return fallback;
}

/**
 * Validates a phone number.
 *
 * Returns `true` if the provided phone number is valid, `null` or `undefined`.
 * This means that `null` or `undefined` input is considered valid (useful for optional fields).
 *
 * @param phoneNumber - The phone number to validate
 * @param defaultCountryCode - Optional country code for local-format numbers
 * @returns `true` if the phone number is valid, `null` or `undefined`, otherwise `false`
 */
export function isPhoneNumberValid(phoneNumber: string | null | undefined, defaultCountryCode?: CountryCode): boolean {
    if (phoneNumber === null || phoneNumber === undefined) {
        return true;
    }
    return parsePhoneNumberFromString(phoneNumber, defaultCountryCode)?.isValid() ?? false;
}

export function normalisePhoneNumberToE164(
    phoneNumber: string | null | undefined,
    defaultCountryCode?: CountryCode,
): string | null {
    if (!phoneNumber) {
        return null;
    }

    const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, defaultCountryCode);
    return parsedPhoneNumber?.isValid() ? parsedPhoneNumber.number : null;
}
